import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MagicLinkEmailSchema, TMagicLinkEmail } from '@onesource/schemas';
import { useRequestMagicLinkMutation } from '@onesource/services';
import { AxiosError } from 'axios';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdOutlineSend } from 'react-icons/md';

const getErrorMessage = (error: Error): string => {
  if (error instanceof AxiosError && error.response?.data?.message) {
    return error.response.data.message;
  }
  return 'Failed to send login link';
};

export interface MagicLinkFormProps {
  /** Absolute origin used to build the link in the email (e.g. window.location.origin) */
  redirectUrl: string;
  /** Original URL the user was trying to reach before being redirected to login (SSO flow) */
  returnUrl?: string;
  /** App key of the originating app (e.g. FIT, RYC) — paired with returnUrl */
  returnApp?: string;
  /** URL for the "Back to Login" link (default: '/auth/login') */
  loginUrl?: string;
  /** Custom title */
  title?: string;
  /** Callback when the email is queued */
  onSuccess?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Custom styles for the form card */
  sx?: SxProps<Theme>;
}

export const MagicLinkForm: FC<MagicLinkFormProps> = ({
  redirectUrl,
  returnUrl,
  returnApp,
  loginUrl = '/auth/login',
  title = 'Login with email',
  onSuccess,
  onError,
  sx,
}) => {
  const mutation = useRequestMagicLinkMutation();

  const { control, handleSubmit, watch } = useForm<TMagicLinkEmail>({
    resolver: zodResolver(MagicLinkEmailSchema),
    defaultValues: { email: '' },
    mode: 'all',
  });

  const watchedEmail = watch('email');

  const onSubmit = handleSubmit(({ email }) => {
    mutation.mutate(
      { email, redirectUrl, returnUrl, returnApp },
      {
        onSuccess: () => onSuccess?.(),
        onError: err => onError?.(err instanceof Error ? err : new Error(String(err))),
      },
    );
  });

  if (mutation.isSuccess) {
    return (
      <Box sx={{ textAlign: 'center', ...sx }}>
        <Typography variant="h5" color="success.main" mb={2} fontWeight="bold">
          Check your email
        </Typography>
        <Typography color="text.secondary" mb={1}>
          If an account exists for:
        </Typography>
        <Typography fontWeight={600} mb={3}>
          {watchedEmail}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          We've sent a login link. It expires in 15 minutes.
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          If you don't see it in your inbox, please check your Spam or Updates folder (in Gmail) and mark the email as
          "Not Spam" so future emails arrive in your inbox.
        </Typography>
        <Link href={loginUrl} sx={{ fontWeight: 600 }}>
          Back to Login
        </Link>
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <Typography variant="h5" textAlign="center" mb={1}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
        Enter your email and we'll send you a one-time login link.
      </Typography>

      <form onSubmit={onSubmit}>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="Email"
              type="email"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              margin="normal"
              autoComplete="email"
              autoFocus
            />
          )}
        />

        {mutation.isError && (
          <Typography color="error" sx={{ mt: 1 }} variant="body2">
            {getErrorMessage(mutation.error)}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={mutation.isPending}
            endIcon={!mutation.isPending ? <MdOutlineSend /> : undefined}
          >
            {mutation.isPending ? <CircularProgress size={24} color="inherit" /> : 'Email me a login link'}
          </Button>
        </Box>
      </form>

      <Box mt={2} textAlign="center">
        <Link href={loginUrl} sx={{ fontWeight: 600, fontSize: '14px' }}>
          Back to Login
        </Link>
      </Box>
    </Box>
  );
};
